import React, { useEffect, useState } from "react";
import Layout from "./components/Layout";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db, trackException } from "./services/firebase";
import { useNavigate } from "react-router-dom";
import {
    makeStyles, shorthands, Persona, Button, Table, TableBody,
    TableHeader, TableRow, TableHeaderCell, TableCell,
    TableCellLayout, Image, TableCellActions, Label
} from "@fluentui/react-components";
import { query, collection, getDocs, where } from "firebase/firestore";
import { EditFilled, EditRegular, bundleIcon, DeleteFilled, DeleteRegular } from "@fluentui/react-icons";
import RestaurantDataService from "./services/restaurantsDataService";
import moment from 'moment';
import ImageDialog from "./components/ImageDialog";
import DeleteEventDialog from "./components/DeleteEventDialog";
import RestaurantForm from "./components/Forms/RestaurantForm";
import rentalLocationDataService from "./services/rentalLocationDataService";
import CustomSearchInput from "./components/CustomSearchInput";
import debounce from 'lodash/debounce';
import { PlusSVG } from "./static/svgs";
import "./components/Forms/FormStyle.css";
import useBreakpoint from "./hooks/useBreakpoint";

const EditIcon = bundleIcon(EditFilled, EditRegular);
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular);

const useStyles = makeStyles({
    root: {
        ...shorthands.overflow("hidden"),
        display: "flex",
        height: "480px",
        backgroundColor: "#fff"
    },

    content: {
        ...shorthands.flex(1),
        ...shorthands.padding("16px"),
        display: "block",
        ...shorthands.gridArea("main")
    },

    breakFlex: {
        flexBasis: "100%",
        textAlign: "left"
    }
});

const columns = [
    { columnKey: "name", label: "Name" },
    { columnKey: "description", label: "Description" },
    { columnKey: "images", label: "Images" }
];

function Restaurants() {
    const styles = useStyles();
    const [user, loading, error] = useAuthState(auth);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedRestaurant, setSelectedRestaurant] = useState();
    const [allrestaurants, setAllRestaurants] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredRestaurant, setFilteredRestaurant] = useState([]);

    const [alllocations, setAllLocations] = useState([]);
    const [open, setOpen] = React.useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
    const navigate = useNavigate();
    const breakpoint = useBreakpoint();
    const isMobile = breakpoint === 'mobile';
    const openForm = (selectedItem) => {
        setIsFormOpen(true);
        setSelectedRestaurant(selectedItem);
    }

    const fetchUserNameAndRestaurants = async () => {
        try {
            if (!!user) {
                const userquery = query(collection(db, "Users"), where("email", "==", user?.email));
                const userdoc = await getDocs(userquery);
                if (userdoc.docs.length > 0) {
                    const restaurantsQuery = await RestaurantDataService.getAllRestaurant();
                    let restaurantArr = restaurantsQuery.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
                    setAllRestaurants(restaurantArr);
                    setFilteredRestaurant(restaurantArr)
                    //console.log(allevents);
                } else {
                    console.log('no access');
                }
            }
        } catch (err) {
            trackException(JSON.stringify(err))
            console.error(err);
            alert("An error occured while fetching user data");
        }
    };

    const fetchAllLocations = async () => {
        try {
            const vehiclesQuery = await rentalLocationDataService.getAllRentalLocation();
            setAllLocations(vehiclesQuery.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
        } catch (error) {
            trackException(JSON.stringify(error))
            console.error(error);
            alert("An error occured while fetching location data");
        }
    }

    useEffect(() => {
        if (loading) return;
        if (!user) return navigate("/");
        fetchUserNameAndRestaurants();
        fetchAllLocations();
    }, [user, loading]);

    const openDeleteForm = (selectedItem) => {
        setIsDeleteDialogOpen(true);
        setSelectedRestaurant(selectedItem);
        // fetchUserNameAndEvents();
    }

    const DeleteRestaurant = async () => {
        RestaurantDataService.deleteRestaurant(selectedRestaurant.id);
        setIsDeleteDialogOpen(false);
        fetchUserNameAndRestaurants();
    };

    // Debounce the handleSearch function
    const debouncedSearch = debounce(search => {
        const lowerSearch = search.toLowerCase();
        const filtered = allrestaurants.filter(event =>
            event.name.toLowerCase().includes(lowerSearch)
        );
        setFilteredRestaurant(filtered);
    }, 300); // Adjust the debounce delay as needed

    const handleSearch = event => {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };
    const handleClear = () => {
        setSearchTerm('');
        debouncedSearch('');
    };

    return (
        <Layout>
            <div className={styles.content}>
                <div className={"d-flex justify-space-between top-content"}>
                    <Button icon={<PlusSVG />} className={"primary-btn"} shape="circular" appearance="primary" onClick={() => openForm(undefined)}>{'New Restaurant'.toUpperCase()}</Button>
                    <CustomSearchInput
                        className={"custom-search-input"}
                        value={searchTerm}
                        onChange={handleSearch}
                        onClick={handleClear}
                    />
                </div>
                <div className={"table-layout"} style={{ border: isMobile ? "none" : undefined }} >
                    <Table aria-label="Table with cell actions">
                        <TableHeader className={"list-row"}>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHeaderCell key={column.columnKey}>
                                        <span className={"listHeaderText"}>{column.label}</span>
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                       <TableBody>
  {filteredRestaurant.length > 0 && filteredRestaurant.map((item) =>
    isMobile ? (
      <div key={item.id} className="restaurant-card">
        <div className="restaurant-card-header">
          <div className="restaurant-card-title">{item.name}</div>
          <div className="restaurant-card-actions">
            <Button
              className="btnBorderOnly"
              shape="circular"
              onClick={() => {
                setOpen(true);
                setSelectedRestaurant(item);
              }}
            >
              Images
            </Button>
            <Button
              icon={<EditIcon />}
              className="edit-icon"
              appearance="subtle"
              aria-label="Edit"
              onClick={() => openForm(item)}
            />
            <Button
              icon={<DeleteIcon />}
              className="delete-icon"
              appearance="subtle"
              aria-label="Delete"
              onClick={() => openDeleteForm(item)}
            />
          </div>
        </div>
         <span className="separator"></span>
        <div className="restaurant-card-row">
          <span className="restaurant-card-label">Description</span>
          <span className="restaurant-card-value">{item.description.substring(0, 150)}</span>
        </div>
      </div>
    ) : (
      <TableRow key={item.id}>
        <TableCell className={"table-row-fonts__primary"}>
          <TableCellLayout>
            {item.name}
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            <Label title={item.description}>{item.description.substring(0, 150)}</Label>
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <Button
            className={"btnBorderOnly"}
            shape="circular"
            onClick={() => {
              setOpen(true);
              setSelectedRestaurant(item);
            }}
          >
            Images
          </Button>
          <TableCellActions>
            <Button
              icon={<EditIcon />}
              appearance="subtle"
              aria-label="Edit"
              onClick={() => openForm(item)}
            />
            <Button
              icon={<DeleteIcon />}
              appearance="subtle"
              aria-label="Delete"
              onClick={() => openDeleteForm(item)}
            />
          </TableCellActions>
        </TableCell>
      </TableRow>
    )
  )}
</TableBody>
                    </Table>
                </div>
            </div>
            <RestaurantForm isOpen={isFormOpen} setIsOpen={(val) => setIsFormOpen(val)} selectedRestaurant={selectedRestaurant}
                updateGrid={fetchUserNameAndRestaurants} locations={alllocations}></RestaurantForm>
            <ImageDialog open={open} setOpen={setOpen} selectedEvent={selectedRestaurant}></ImageDialog>
            <DeleteEventDialog isDeleteDialogOpen={isDeleteDialogOpen}
                setIsDeleteDialogOpen={setIsDeleteDialogOpen} DeleteEvent={DeleteRestaurant} text={'Restaurant'} />
        </Layout>
    )
}

export default Restaurants;